import React, { useState } from "react";
import { Sparkles, X, Loader2, RefreshCw } from "lucide-react";
import { supabase } from "./supabaseClient";

// --- per-tab/session "seen" cache ---
const SEEN_KEY = "surprise_seen_codes";

function loadSeen() {
  try {
    return new Set(JSON.parse(sessionStorage.getItem(SEEN_KEY) || "[]"));
  } catch {
    return new Set();
  }
}

function saveSeen(seenSet) {
  sessionStorage.setItem(SEEN_KEY, JSON.stringify([...seenSet]));
}

function clearSeen() {
  sessionStorage.removeItem(SEEN_KEY);
}

const SurpriseButton = ({ semester /* e.g., "2425F" */ }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false); // covers initial + regenerate
  const [recommendation, setRecommendation] = useState(null);
  const [error, setError] = useState(null);

  // single fetcher used by initial click + regenerate
  const fetchRecommendation = async ({ openModal = true } = {}) => {
    setIsLoading(true);
    setError(null);

    try {
      const raw = process.env.REACT_APP_BACKEND_URL;
      if (!raw) throw new Error("Backend URL not configured");
      const backendUrl = raw.replace(/\/$/, "");

      // auth
      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession();
      if (sessionError || !session)
        throw new Error("Please sign in to get recommendations");
      const token = session.access_token;

      // load seen codes for this tab/session
      const seen = loadSeen();

      // nonce nudges backend randomness (harmless if ignored)
      const body = {
        ...(semester ? { semester } : {}),
        exclude_codes: [...seen],
        nonce: Date.now(),
      };

      const res = await fetch(`${backendUrl}/surprise_recommendation`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      const maybeJson = await res.json().catch(() => ({}));
      if (!res.ok) {
        // 500 here almost always means the local backend can't reach Qdrant
        // (the vector DB only runs in production). Give an honest message.
        if (res.status >= 500) {
          throw new Error(
            "Surprise Me uses the vector database, which only runs in production. " +
              "It'll work on the live site — locally we'd need Qdrant running."
          );
        }
        const msg = maybeJson?.error || maybeJson?.details || "Failed to get recommendation";
        throw new Error(msg);
      }

      setRecommendation(maybeJson);

      // mark returned course codes as seen for this session
      if (Array.isArray(maybeJson.course_codes)) {
        for (const code of maybeJson.course_codes) {
          if (code) seen.add(String(code).trim().toUpperCase());
        }
        saveSeen(seen);
      }

      if (openModal) setIsOpen(true);
    } catch (err) {
      console.error("Error getting surprise recommendation:", err);
      setRecommendation(null);
      setError(err.message || "Something went wrong");
      setIsOpen(true); // show error in modal
    } finally {
      setIsLoading(false);
    }
  };

  const handleSurpriseMe = () => fetchRecommendation({ openModal: true });
  const handleRegenerate = () => fetchRecommendation({ openModal: false });

  const closeModal = () => {
    setIsOpen(false);
    setRecommendation(null);
    setError(null);
  };

  const handleResetSeen = () => {
    clearSeen();
    setError(null);
    // Optionally trigger a new recommendation immediately:
    // fetchRecommendation({ openModal: false });
  };

  return (
    <>
      {/* Floating Surprise Me — sits above the bottom rail so it never overlaps */}
      <button
        onClick={handleSurpriseMe}
        disabled={isLoading}
        aria-busy={isLoading}
        className="surprise-btn fixed bottom-[6.5rem] right-5 z-40 inline-flex items-center gap-2 rounded-full border border-primary/30 bg-surface/95 px-4 py-2 text-sm font-semibold text-primary shadow-card backdrop-blur transition-all duration-200 hover:bg-primary hover:text-white hover:shadow-lift active:translate-y-px disabled:cursor-not-allowed disabled:opacity-60"
        title="Get a surprising course recommendation"
      >
        {isLoading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <Sparkles className="h-4 w-4" />
        )}
        Surprise me
      </button>

      {/* Modal */}
      {isOpen && (
        <div className="modal-overlay" role="dialog" aria-modal="true" onClick={closeModal}>
          <div className="modal-panel max-w-md" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="flex items-center justify-between border-b border-line px-6 py-4">
              <h2 className="flex items-center gap-2 font-display text-xl font-semibold text-ink">
                <Sparkles className="h-5 w-5 text-accent" />
                Surprise Recommendation
              </h2>
              <button
                onClick={closeModal}
                className="-mr-1 rounded-md p-1.5 text-ink-soft transition-colors hover:bg-surface-alt hover:text-ink"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Content */}
            <div className="space-y-4 px-6 py-5">
              {error ? (
                <div className="rounded-xl border border-danger/30 bg-danger-soft p-4 text-center">
                  <p className="font-semibold text-danger">Oops! Something went wrong</p>
                  <p className="mt-1 text-sm text-ink-muted">{error}</p>

                  {/* Show Reset link when we've exhausted unseen options */}
                  {/no unseen/i.test(error) && (
                    <button
                      onClick={handleResetSeen}
                      className="mt-3 text-sm font-medium text-primary underline underline-offset-2 hover:text-primary-dark"
                    >
                      Reset suggestions for this session
                    </button>
                  )}
                </div>
              ) : recommendation ? (
                <>
                  {/* Course Info */}
                  <div className="rounded-xl border border-line bg-surface-alt p-4">
                    <h3 className="mb-2 font-display text-lg font-semibold text-ink">
                      {recommendation.course_codes?.join(" / ")} —{" "}
                      {recommendation.course_title}
                    </h3>
                    <div className="mb-3 flex flex-wrap gap-2">
                      {recommendation.department && (
                        <span className="badge-primary">{recommendation.department}</span>
                      )}
                      {recommendation.semester && (
                        <span className="badge-neutral">{recommendation.semester}</span>
                      )}
                    </div>
                    {recommendation.description && (
                      <p className="text-sm leading-relaxed text-ink-muted">
                        {recommendation.description}
                      </p>
                    )}
                  </div>

                  {/* Surprise Connection */}
                  {recommendation.surprise_connection && (
                    <div className="rounded-xl border border-accent/30 bg-accent-soft p-4">
                      <h4 className="mb-1.5 flex items-center gap-1.5 font-semibold text-accent-dark">
                        <Sparkles className="h-4 w-4" />
                        Why this might surprise you
                      </h4>
                      <p className="text-sm leading-relaxed text-ink">
                        {recommendation.surprise_connection}
                      </p>
                    </div>
                  )}
                </>
              ) : (
                <div className="flex h-16 items-center justify-center text-sm text-ink-muted">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Finding something for you…
                </div>
              )}

              {/* Actions: Regenerate + Close only */}
              <div className="flex gap-3 pt-1">
                <button
                  onClick={handleRegenerate}
                  disabled={isLoading}
                  className="btn-secondary flex-1"
                  title="Try a different pick"
                >
                  {isLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4" />
                  )}
                  Regenerate
                </button>
                <button onClick={closeModal} className="btn-primary flex-1">
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default SurpriseButton;
